import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Badge, Button, DatePicker, Icon, Label, Textarea, cn } from "@nexus/ui";

import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { INITIAL_OPPORTUNITY_FILTERS } from "@/features/opportunities/constants/opportunity-filters";
import { isUsableScriptText } from "@/features/opportunities/lib/script-display-title";
import { ApiError } from "@/lib/api-client";
import { useRetryOrder, useUpdateOrderEta } from "@/lib/queries";

import { getVendorInitials } from "../lib/vendorInitials";

import type { MarketplaceOrder, MarketplaceOrderStatus } from "../types/marketplace.types";

/** How long the deep-link highlight ring stays before fading out (ms). */
const HIGHLIGHT_DURATION_MS = 2400;
const ETA_NOTE_MAX_LENGTH = 500;
const ETA_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Local calendar `YYYY-MM-DD` — never `toISOString()`, which shifts across UTC. */
function localTodayYmd(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Format a stored `YYYY-MM-DD` for display. Built from local calendar parts so
 * `new Date("YYYY-MM-DD")` cannot shift the day at UTC midnight.
 */
function formatEtaDate(ymd: string): string {
  const match = ETA_DATE_ONLY.exec(ymd);
  if (!match) return ymd;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function parseEtaDate(ymd: string): Date | undefined {
  const match = ETA_DATE_ONLY.exec(ymd);
  if (!match) return undefined;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function toEtaDateInput(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function isEtaOverdue(etaDate: string): boolean {
  return etaDate < localTodayYmd();
}

const STATUS_VARIANT: Record<
  MarketplaceOrderStatus,
  "status-sent" | "status-in-production" | "status-error"
> = {
  PENDING_SEND: "status-in-production",
  SENDING: "status-in-production",
  SENT: "status-sent",
  FAILED: "status-error",
};

function canRetryHandoff(status: MarketplaceOrderStatus): boolean {
  return status === "FAILED" || status === "PENDING_SEND" || status === "SENDING";
}

interface OrderCardProps {
  order: MarketplaceOrder;
  tenantId: string;
  /** Deep-link target — auto-expands, scrolls into view, and pulses a ring. */
  highlight?: boolean;
}

export function OrderCard({ order, tenantId, highlight = false }: OrderCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = usePermissions();
  const retryOrder = useRetryOrder();
  const updateEta = useUpdateOrderEta();
  const [expanded, setExpanded] = useState(highlight);
  const [ringVisible, setRingVisible] = useState(highlight);
  const [draftDate, setDraftDate] = useState(order.etaDate ?? "");
  const [draftNote, setDraftNote] = useState(order.etaNote ?? "");
  const cardRef = useRef<HTMLDivElement>(null);
  const etaDateFieldId = useId();
  const etaNoteFieldId = useId();
  const etaNoteHintId = useId();
  const scriptsHeadingId = useId();
  const displayDateTime = formatDateTime(order.sentAt ?? order.createdAt);
  const initials = getVendorInitials(order.vendor.name);
  const scriptCount = order.scripts.length;
  const showHandoffAlert =
    order.status === "FAILED" ||
    order.status === "PENDING_SEND" ||
    order.status === "SENDING";
  const retryable = canRetryHandoff(order.status);
  const canEditEta = useIamPermission(perms.MARKETPLACE_UPDATE_ORDER_ETA);
  const etaPending = updateEta.isPending;
  const hasSavedEta = order.etaDate !== null || order.etaNote !== null;
  const trimmedDraftNote = draftNote.trim();
  const savedNote = order.etaNote ?? "";
  const etaUnchanged = draftDate === (order.etaDate ?? "") && trimmedDraftNote === savedNote;
  const canSaveEta = draftDate !== "" && !etaPending && !etaUnchanged;
  const canClearEta = hasSavedEta && !etaPending;
  const noteWithoutDate = trimmedDraftNote !== "" && draftDate === "";
  const dateInvalid = noteWithoutDate;
  const etaForbidden =
    updateEta.error instanceof ApiError && updateEta.error.status === 403;
  const etaOverdue = order.etaDate !== null && isEtaOverdue(order.etaDate);

  const openScript = (setId: string, scriptId: string, source?: "legacy" | "persona") => {
    if (source === "persona") {
      void navigate({
        to: "/persona-opportunities",
        search: { setId, tab: "scripts" },
      });
      return;
    }
    void navigate({
      to: "/opportunities",
      search: {
        ...INITIAL_OPPORTUNITY_FILTERS,
        setId,
        tab: "completed_scripts",
        q: "",
        scriptId,
      },
    });
  };

  const handleRetry = () => {
    retryOrder.mutate({ orderId: order.id, tenantId });
  };

  const handleSaveEta = () => {
    if (!canSaveEta) return;
    updateEta.mutate({
      orderId: order.id,
      tenantId,
      etaDate: draftDate,
      etaNote: trimmedDraftNote === "" ? null : trimmedDraftNote,
    });
  };

  const handleClearEta = () => {
    if (!canClearEta) return;
    updateEta.mutate({
      orderId: order.id,
      tenantId,
      etaDate: null,
      etaNote: null,
    });
  };

  useEffect(() => {
    setDraftDate(order.etaDate ?? "");
    setDraftNote(order.etaNote ?? "");
  }, [order.etaDate, order.etaNote]);

  useEffect(() => {
    if (!highlight) return;
    cardRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    const timer = window.setTimeout(() => setRingVisible(false), HIGHLIGHT_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [highlight]);

  return (
    <div
      ref={cardRef}
      data-state={expanded ? "open" : "closed"}
      className={cn(
        "group/order scroll-mt-4 overflow-hidden rounded-xl border bg-card transition-[border-color,box-shadow] duration-200 ease-out",
        ringVisible
          ? "border-primary/40 ring-2 ring-primary/40"
          : "border-border hover:border-foreground/15",
      )}
    >
      <button
        type="button"
        className="flex w-full cursor-pointer items-center gap-3.5 px-4 py-3.5 text-left transition-colors duration-150 ease-out hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ring)]"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {order.vendor.logoUrl ? (
          <img
            src={order.vendor.logoUrl}
            alt={order.vendor.name}
            loading="lazy"
            className="h-11 w-11 shrink-0 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-semibold text-muted-foreground">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-foreground">{order.vendor.name}</p>
            <Badge variant={STATUS_VARIANT[order.status]} dot>
              {t(`marketplace.orders.status.${order.status}`)}
            </Badge>
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="file-text" size="xs" className="text-muted-foreground/70" aria-hidden />
            {t("marketplace.orders.scriptCount", { count: scriptCount })}
            <span className="text-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="shrink-0">
              {order.sentAt
                ? t("marketplace.orders.detail.sentOn", "Sent")
                : t("marketplace.orders.detail.queuedOn", "Queued")}
            </span>
            <time dateTime={order.sentAt ?? order.createdAt} className="tabular-nums">
              {displayDateTime}
            </time>
          </p>
          <p className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            {order.etaDate ? (
              <>
                <Icon
                  name="calendar"
                  size="xs"
                  className="shrink-0 text-muted-foreground/70"
                  aria-hidden
                />
                <span className="shrink-0">
                  {t("marketplace.orders.eta.metaLabel", "ETA")}
                </span>
                <time
                  dateTime={order.etaDate}
                  className={cn(
                    "shrink-0 tabular-nums text-foreground",
                    etaOverdue && "text-destructive",
                  )}
                >
                  {formatEtaDate(order.etaDate)}
                </time>
                {etaOverdue && (
                  <Badge variant="status-error">
                    {t("marketplace.orders.eta.overdue", "Overdue")}
                  </Badge>
                )}
                {order.etaNote && (
                  <>
                    <span className="text-muted-foreground/40" aria-hidden>
                      ·
                    </span>
                    <span className="min-w-0 truncate">{order.etaNote}</span>
                  </>
                )}
              </>
            ) : (
              t("marketplace.orders.eta.empty", "ETA will be determined soon")
            )}
          </p>
        </div>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors duration-150 ease-out group-hover/order:bg-muted">
          <Icon
            name="chevron-down"
            size="sm"
            className={cn("transition-transform duration-200 ease-out", expanded && "rotate-180")}
          />
        </span>
      </button>

      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col px-4 pb-5 pt-2">
            {showHandoffAlert && (
              <div
                className={cn(
                  "mb-6 rounded-md border px-3 py-2.5",
                  order.status === "FAILED"
                    ? "border-destructive/30 bg-destructive/5"
                    : "border-border bg-muted/30",
                )}
                role="status"
              >
                <p
                  className={cn(
                    "text-sm",
                    order.status === "FAILED"
                      ? "font-medium text-destructive"
                      : "text-foreground",
                  )}
                >
                  {t(`marketplace.orders.handoff.${order.status}.title`)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(`marketplace.orders.handoff.${order.status}.description`)}
                </p>
                {retryable && (
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={order.status === "FAILED" ? "default" : "outline"}
                      className="min-h-11"
                      disabled={retryOrder.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRetry();
                      }}
                    >
                      {retryOrder.isPending
                        ? t("marketplace.orders.handoff.retrying")
                        : t("marketplace.orders.handoff.retry")}
                    </Button>
                    {order.status === "FAILED" && (
                      <span className="text-xs text-muted-foreground">
                        {t("marketplace.orders.handoff.supportHint")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            {expanded && canEditEta && (
              <section
                className="flex flex-col gap-2.5"
                aria-labelledby={`${etaDateFieldId}-heading`}
                aria-busy={etaPending}
              >
                <p
                  id={`${etaDateFieldId}-heading`}
                  className="text-sm font-semibold tracking-tight text-foreground"
                >
                  {t("marketplace.orders.eta.section", "Delivery ETA")}
                </p>
                <div className="grid gap-3 sm:grid-cols-[minmax(14rem,20rem)_minmax(0,1fr)] sm:items-start sm:gap-5">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-baseline gap-1">
                      <Label htmlFor={etaDateFieldId} className="text-xs font-medium text-foreground">
                        {t("marketplace.orders.eta.dateLabel", "Date")}
                      </Label>
                      <span className="text-xs text-destructive" aria-hidden>
                        *
                      </span>
                    </div>
                    <DatePicker
                      id={etaDateFieldId}
                      value={parseEtaDate(draftDate)}
                      disabled={etaPending}
                      aria-required
                      aria-invalid={dateInvalid}
                      aria-describedby={dateInvalid ? etaNoteHintId : undefined}
                      onChange={(date) => setDraftDate(date ? toEtaDateInput(date) : "")}
                      triggerClassName="h-11 min-h-11 text-xs"
                      labels={{
                        placeholder: t("marketplace.orders.eta.datePlaceholder", "Pick a date"),
                        clear: t("marketplace.orders.eta.dateClear", "Clear date"),
                        today: t("marketplace.orders.eta.dateToday", "Today"),
                      }}
                    />
                    {noteWithoutDate && (
                      <p id={etaNoteHintId} role="alert" className="text-xs text-destructive">
                        {t(
                          "marketplace.orders.eta.noteRequiresDate",
                          "Add a date before adding a note.",
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-baseline gap-1">
                      <Label htmlFor={etaNoteFieldId} className="text-xs font-normal text-muted-foreground">
                        {t("marketplace.orders.eta.noteLabel", "Note")}
                      </Label>
                      <span className="text-xs font-normal text-muted-foreground">
                        ({t("marketplace.orders.eta.noteOptional", "optional")})
                      </span>
                    </div>
                    <Textarea
                      id={etaNoteFieldId}
                      value={draftNote}
                      disabled={etaPending}
                      rows={2}
                      maxLength={ETA_NOTE_MAX_LENGTH}
                      placeholder={t(
                        "marketplace.orders.eta.notePlaceholder",
                        "e.g. week of the shoot",
                      )}
                      className="min-h-11 resize-y rounded-sm shadow-none"
                      onChange={(event) => setDraftNote(event.target.value)}
                    />
                  </div>
                </div>
                {etaForbidden && (
                  <p role="alert" className="text-xs text-destructive">
                    {t(
                      "marketplace.orders.eta.forbidden",
                      "You don't have permission to update this ETA.",
                    )}
                  </p>
                )}
                <div className="mt-0.5 flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canSaveEta}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSaveEta();
                      }}
                    >
                      {etaPending
                        ? t("marketplace.orders.eta.saving", "Saving…")
                        : t("marketplace.orders.eta.save", "Save ETA")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground"
                      disabled={!canClearEta}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClearEta();
                      }}
                    >
                      {t("marketplace.orders.eta.clear", "Clear ETA")}
                    </Button>
                  </div>
                  <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Icon
                      name="mail"
                      size="xs"
                      className="shrink-0 text-muted-foreground/70"
                      aria-hidden
                    />
                    {t(
                      "marketplace.orders.eta.emailHint",
                      "We'll email everyone copied on this order.",
                    )}
                  </p>
                </div>
              </section>
            )}
            <section
              className={cn(
                "flex flex-col gap-1.5",
                (showHandoffAlert || canEditEta) && "mt-8",
              )}
              aria-labelledby={scriptsHeadingId}
            >
              <p
                id={scriptsHeadingId}
                className={cn(
                  "text-xs font-medium",
                  canEditEta ? "text-muted-foreground" : "text-foreground",
                )}
              >
                {t("marketplace.orders.detail.scripts")}
              </p>
              <ul className="flex flex-col">
                {order.scripts.map((s) => {
                  // Concept-first title (marketplace), but scrub LLM null-sentinels (AD-2343).
                  const concept = isUsableScriptText(s.script.concept)
                    ? s.script.concept!.trim()
                    : null;
                  const hook = isUsableScriptText(s.script.hook)
                    ? s.script.hook!.trim()
                    : null;
                  const title = concept || hook || t("marketplace.orders.detail.unnamedScript");
                  const subtitle = concept && hook ? hook : null;
                  return (
                    <li
                      key={s.scriptId}
                      className="flex items-center gap-3 rounded-sm py-1 transition-colors duration-150 ease-out hover:bg-muted/50"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs text-foreground">{title}</p>
                        {subtitle && (
                          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</p>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        iconEnd="arrow-up-right"
                        className="shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          const setId = s.script.opportunity.setId;
                          if (!setId) return;
                          openScript(setId, s.scriptId, s.script.source);
                        }}
                      >
                        {t("marketplace.orders.detail.viewScript")}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
